// Reports (BRIEF.md section 12, M5 plan D7): pick a report and a period, read it, open the
// transactions behind any figure, download it for the accountant.
import { useEffect, useState } from 'react';
import type { Api } from '../api.js';
import { day, dollars } from '../format.js';

type ReportId = 'profit-and-loss' | 'balance-sheet' | 'cash' | 'director-loan';
const REPORTS: [ReportId, string][] = [
  ['profit-and-loss', 'Profit and loss'],
  ['balance-sheet', 'Balance sheet'],
  ['cash', 'Cash summary'],
  ['director-loan', 'Money to or from the director'],
];

interface Section {
  title: string;
  rows: { accountId: string | null; code: string | null; name: string; values: string[] }[];
  total: string[];
}
interface Pl {
  columns: { label: string; from: string; to: string }[];
  sections: Section[];
  netProfit: string[];
}
interface Bs {
  asAt: string;
  sections: Section[];
  netAssets: string;
  difference: string;
}
interface Account {
  accountId: string;
  code: string;
  name: string;
  opening: string;
  closing: string;
  entries: {
    date: string;
    journalId: string;
    memo: string | null;
    description: string | null;
    amount: string;
    balance: string;
  }[];
}
interface Cash {
  accounts: {
    accountId: string;
    code: string;
    name: string;
    opening: string;
    moneyIn: string;
    moneyOut: string;
    closing: string;
  }[];
}
interface DirectorLoan {
  account: Account | null;
  owedByDirector: boolean;
}

const iso = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

/** Period presets, Australian financial year (July to June) and quarters. */
function presets(): [string, string, string][] {
  const now = new Date();
  const y = now.getFullYear();
  const m = now.getMonth();
  const monthStart = (yy: number, mm: number) => iso(new Date(yy, mm, 1));
  const monthEnd = (yy: number, mm: number) => iso(new Date(yy, mm + 1, 0));
  const q = Math.floor(m / 3) * 3;
  const fy = m >= 6 ? y : y - 1;
  return [
    ['This month', monthStart(y, m), iso(now)],
    ['Last month', monthStart(y, m - 1), monthEnd(y, m - 1)],
    ['This quarter', monthStart(y, q), iso(now)],
    ['Last quarter', monthStart(y, q - 3), monthEnd(y, q - 1)],
    ['This financial year', `${fy}-07-01`, iso(now)],
    ['Last financial year', `${fy - 1}-07-01`, `${fy}-06-30`],
  ];
}

export function Reports({ api, orgId }: { api: Api; orgId: string }) {
  const options = presets();
  const [report, setReport] = useState<ReportId>('profit-and-loss');
  const [from, setFrom] = useState(options[4]?.[1] ?? '');
  const [to, setTo] = useState(options[4]?.[2] ?? '');
  const [compare, setCompare] = useState<'' | 'previous' | 'last_year'>('');
  const [byProduct, setByProduct] = useState(false);
  const [data, setData] = useState<unknown>(null);
  const [error, setError] = useState('');
  const [drill, setDrill] = useState<{
    accountId: string;
    name: string;
    from: string;
    to: string;
  } | null>(null);

  const query =
    report === 'balance-sheet'
      ? `?asAt=${to}`
      : `?from=${from}&to=${to}${report === 'profit-and-loss' && compare ? `&compare=${compare}` : ''}${report === 'profit-and-loss' && byProduct ? '&by=product' : ''}`;
  const path = `/reports/${report}${query}`;

  useEffect(() => {
    let live = true;
    setData(null);
    setError('');
    api
      .get<unknown>(orgId, path)
      .then((d) => live && setData(d))
      .catch(
        (e) => live && setError(e instanceof Error ? e.message : 'Could not load the report.'),
      );
    return () => {
      live = false;
    };
  }, [api, orgId, path]);

  async function download(format: 'csv' | 'xlsx') {
    try {
      await api.download(
        orgId,
        `${path}&format=${format}`,
        `${report}-${report === 'balance-sheet' ? to : `${from}-${to}`}.${format}`,
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : 'The file could not be downloaded.');
    }
  }

  return (
    <section className="stack">
      <h1>Reports</h1>
      <div className="title-row wrap">
        <select
          value={report}
          onChange={(e) => {
            setReport(e.target.value as ReportId);
            setDrill(null);
          }}
        >
          {REPORTS.map(([id, label]) => (
            <option key={id} value={id}>
              {label}
            </option>
          ))}
        </select>
        <select
          value=""
          onChange={(e) => {
            const p = options.find(([label]) => label === e.target.value);
            if (p) {
              setFrom(p[1]);
              setTo(p[2]);
              setDrill(null);
            }
          }}
        >
          <option value="">Choose a period</option>
          {options.map(([label]) => (
            <option key={label}>{label}</option>
          ))}
        </select>
        {report !== 'balance-sheet' && (
          <input
            type="date"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            aria-label="From"
          />
        )}
        <input
          type="date"
          value={to}
          onChange={(e) => setTo(e.target.value)}
          aria-label={report === 'balance-sheet' ? 'As at' : 'To'}
        />
      </div>
      {report === 'profit-and-loss' && (
        <div className="title-row wrap">
          <select
            value={compare}
            onChange={(e) => {
              setCompare(e.target.value as typeof compare);
              setByProduct(false);
            }}
          >
            <option value="">No comparison</option>
            <option value="previous">Compare with the period before</option>
            <option value="last_year">Compare with last year</option>
          </select>
          <label className="inline">
            <input
              type="checkbox"
              checked={byProduct}
              onChange={(e) => {
                setByProduct(e.target.checked);
                setCompare('');
              }}
            />
            By product
          </label>
        </div>
      )}
      <div className="title-row">
        <button type="button" className="link" onClick={() => download('xlsx')}>
          Download Excel
        </button>
        <button type="button" className="link" onClick={() => download('csv')}>
          Download CSV
        </button>
      </div>
      <AccountantPack api={api} orgId={orgId} onError={setError} />
      {error && <p className="error">{error}</p>}
      {!data && !error && <p className="muted">Loading</p>}
      {data !== null && report === 'profit-and-loss' && (
        <SectionsTable
          columns={(data as Pl).columns.map((c) => c.label)}
          sections={(data as Pl).sections}
          footer={['Net profit', (data as Pl).netProfit]}
          onOpen={(row, i) => {
            const c = (data as Pl).columns[i] ?? (data as Pl).columns[0];
            if (row.accountId && c)
              setDrill({ accountId: row.accountId, name: row.name, from: c.from, to: c.to });
          }}
        />
      )}
      {data !== null && report === 'balance-sheet' && (
        <>
          <SectionsTable
            columns={[`As at ${day((data as Bs).asAt)}`]}
            sections={(data as Bs).sections}
            footer={['Net assets', [(data as Bs).netAssets]]}
            onOpen={(row) =>
              row.accountId &&
              setDrill({ accountId: row.accountId, name: row.name, from: '1900-01-01', to })
            }
          />
          {(data as Bs).difference !== '0.00' && (
            <p className="error">
              The balance sheet is out by {dollars((data as Bs).difference)}. Tell your accountant.
            </p>
          )}
        </>
      )}
      {data !== null && report === 'cash' && (
        <table className="table">
          <thead>
            <tr>
              <th>Account</th>
              <th className="num">Start</th>
              <th className="num">Money in</th>
              <th className="num">Money out</th>
              <th className="num">End</th>
            </tr>
          </thead>
          <tbody>
            {(data as Cash).accounts.map((a) => (
              <tr
                key={a.accountId}
                className="clickable"
                onClick={() => setDrill({ accountId: a.accountId, name: a.name, from, to })}
              >
                <td>{a.name}</td>
                <td className="num">{dollars(a.opening)}</td>
                <td className="num">{dollars(a.moneyIn)}</td>
                <td className="num">{dollars(a.moneyOut)}</td>
                <td className="num">{dollars(a.closing)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {data !== null && report === 'director-loan' && (
        <DirectorLoanView data={data as DirectorLoan} />
      )}
      {drill && <Transactions api={api} orgId={orgId} {...drill} onClose={() => setDrill(null)} />}
    </section>
  );
}

function SectionsTable(props: {
  columns: string[];
  sections: Section[];
  footer: [string, string[]];
  onOpen: (row: Section['rows'][number], column: number) => void;
}) {
  const { columns, sections, footer } = props;
  return (
    <table className="table">
      <thead>
        <tr>
          <th />
          {columns.map((c) => (
            <th key={c} className="num">
              {c}
            </th>
          ))}
        </tr>
      </thead>
      {sections.map((s) => (
        <tbody key={s.title}>
          <tr>
            <th colSpan={columns.length + 1}>{s.title}</th>
          </tr>
          {s.rows.map((r) => (
            <tr key={r.accountId ?? r.name} className={r.accountId ? 'clickable' : undefined}>
              <td>{r.name}</td>
              {r.values.map((v, i) => (
                <td key={columns[i]} className="num">
                  {r.accountId ? (
                    <button type="button" className="link" onClick={() => props.onOpen(r, i)}>
                      {dollars(v)}
                    </button>
                  ) : (
                    dollars(v)
                  )}
                </td>
              ))}
            </tr>
          ))}
          <tr className="total">
            <td>Total {s.title.toLowerCase()}</td>
            {s.total.map((v, i) => (
              <td key={columns[i]} className="num">
                {dollars(v)}
              </td>
            ))}
          </tr>
        </tbody>
      ))}
      <tfoot>
        <tr className="total">
          <td>{footer[0]}</td>
          {footer[1].map((v, i) => (
            <td key={columns[i]} className="num">
              {dollars(v)}
            </td>
          ))}
        </tr>
      </tfoot>
    </table>
  );
}

function DirectorLoanView({ data }: { data: DirectorLoan }) {
  if (!data.account)
    return <p className="muted">Nothing went to or from the director in this period.</p>;
  const a = data.account;
  return (
    <div className="stack">
      <p>
        {data.owedByDirector
          ? `The director owes the company ${dollars(a.closing)}. If this is still owed at 30 June, talk to your accountant about Division 7A before the tax return is lodged.`
          : `The company owes the director ${dollars(a.closing.replace('-', ''))}.`}
      </p>
      <EntriesTable account={a} />
    </div>
  );
}

function Transactions(props: {
  api: Api;
  orgId: string;
  accountId: string;
  name: string;
  from: string;
  to: string;
  onClose: () => void;
}) {
  const [account, setAccount] = useState<Account | null>(null);
  useEffect(() => {
    props.api
      .get<{ accounts: Account[] }>(
        props.orgId,
        `/reports/transactions?accountId=${props.accountId}&from=${props.from}&to=${props.to}`,
      )
      .then((r) => setAccount(r.accounts[0] ?? null))
      .catch(() => setAccount(null));
  }, [props.api, props.orgId, props.accountId, props.from, props.to]);
  return (
    <div className="stack bank-card">
      <div className="title-row">
        <h2>{props.name}</h2>
        <button type="button" className="link" onClick={props.onClose}>
          Close
        </button>
      </div>
      {account ? <EntriesTable account={account} /> : <p className="muted">Loading</p>}
    </div>
  );
}

function EntriesTable({ account }: { account: Account }) {
  return (
    <table className="table">
      <tbody>
        <tr className="muted">
          <td colSpan={3}>Start</td>
          <td className="num">{dollars(account.opening)}</td>
        </tr>
        {account.entries.map((e) => (
          // The running balance changes on every line, so it tells two lines of one entry apart.
          <tr key={`${e.journalId}-${e.balance}`}>
            <td>{day(e.date)}</td>
            <td>{e.description ?? e.memo ?? ''}</td>
            <td className="num">{dollars(e.amount)}</td>
            <td className="num">{dollars(e.balance)}</td>
          </tr>
        ))}
        <tr className="total">
          <td colSpan={3}>End</td>
          <td className="num">{dollars(account.closing)}</td>
        </tr>
      </tbody>
    </table>
  );
}

/** The year-end pack for the accountant: one ZIP per financial year (M5 plan D9). */
function AccountantPack(props: { api: Api; orgId: string; onError: (m: string) => void }) {
  const now = new Date();
  const lastFinished = now.getMonth() >= 6 ? now.getFullYear() : now.getFullYear() - 1;
  const [year, setYear] = useState(lastFinished);
  const [busy, setBusy] = useState(false);
  async function get() {
    setBusy(true);
    try {
      await props.api.download(
        props.orgId,
        `/reports/accountant-pack?year=${year}`,
        `accountant-pack-FY${year}.zip`,
      );
    } catch (e) {
      props.onError(e instanceof Error ? e.message : 'The pack could not be downloaded.');
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className="title-row wrap">
      <span>Year-end pack for your accountant:</span>
      <select value={year} onChange={(e) => setYear(Number(e.target.value))}>
        {[lastFinished + 1, lastFinished, lastFinished - 1].map((y) => (
          <option key={y} value={y}>
            {`July ${y - 1} to June ${y}`}
            {y > lastFinished ? ' (so far)' : ''}
          </option>
        ))}
      </select>
      <button type="button" className="link" disabled={busy} onClick={get}>
        {busy ? 'Preparing' : 'Download'}
      </button>
    </div>
  );
}
